import React, { useState, useMemo, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { DASTicketDetails, ConsolidatedChildItem } from '../../../../api/types/dasTickets.types';
import { TicketBasicInfo } from './shared/TicketBasicInfo';
import { TicketAttachments } from './shared/TicketAttachments';
import { TicketHistory } from './shared/TicketHistory';
import { TicketApprovalFlow } from './shared/TicketApprovalFlow';
import { TicketRelated } from './shared/TicketRelated';
import { TicketConsolidatedItems } from './shared/TicketConsolidatedItems';
import { ApprovalDocumentModal, transformTicketToApprovalDocument } from './approval-document';
import { consolidationService } from '../../../../api/services/das/consolidationService';

interface CompletedTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: DASTicketDetails;
}

type TabId = 'outcome' | 'approval-flow' | 'history' | 'attachments' | 'related' | 'consolidated';

interface TabConfig {
  id: TabId;
  label: string;
  icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
  badge?: number;
}

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const CompletedTicketModal: React.FC<CompletedTicketModalProps> = ({ isOpen, onClose, ticket }) => {
  const [activeTab, setActiveTab] = useState<TabId>('outcome');
  const [showApprovalDocument, setShowApprovalDocument] = useState(false);
  const [consolidatedItems, setConsolidatedItems] = useState<ConsolidatedChildItem[]>([]);
  const [consolidatedItemsLoading, setConsolidatedItemsLoading] = useState(false);

  // Fetch consolidated items when modal opens (for consolidated requests)
  useEffect(() => {
    const fetchConsolidatedItems = async () => {
      // Multiple ways to detect a consolidated request:
      // 1. isConsolidatedRequest flag from backend
      // 2. consolidatedItemCount > 0
      // 3. currentStage contains 'consolidation' (e.g., "Closed - Approved via consolidation")
      const stageIndicatesConsolidation = ticket.currentStage.toLowerCase().includes('consolidation');
      const hasConsolidatedCount = ticket.consolidatedItemCount !== undefined && ticket.consolidatedItemCount > 0;

      console.log('[CompletedTicketModal] Checking for consolidated items:', {
        isOpen,
        isConsolidatedRequest: ticket.isConsolidatedRequest,
        consolidatedItemCount: ticket.consolidatedItemCount,
        currentStage: ticket.currentStage,
        stageIndicatesConsolidation,
        ticketId: ticket.id,
      });

      // Try to fetch if any indicator suggests this is a consolidated request
      const shouldFetch = isOpen && (
        ticket.isConsolidatedRequest ||
        hasConsolidatedCount ||
        stageIndicatesConsolidation
      );

      if (!shouldFetch) {
        console.log('[CompletedTicketModal] Skipping fetch - not a consolidated request');
        return;
      }

      try {
        setConsolidatedItemsLoading(true);
        console.log('[CompletedTicketModal] Fetching consolidated items for ticket:', ticket.id);
        const response = await consolidationService.getChildren(ticket.id.toString());
        console.log('[CompletedTicketModal] API response:', response);

        // Map the response to ConsolidatedChildItem format
        const mappedItems: ConsolidatedChildItem[] = response.map((req: any) => ({
          id: req.id,
          ticketNo: req.requestNumber,
          subject: req.title,
          value: req.requestValue,
          status: req.currentStatus,
          createdByUserName: req.submittedByName || 'Unknown',
          preApprovedAt: req.preApprovedAt,
          preApprovedByName: req.preApprovedByName,
        }));
        console.log('[CompletedTicketModal] Mapped items:', mappedItems);
        setConsolidatedItems(mappedItems);
      } catch (err) {
        console.error('Failed to fetch consolidated items for approval document:', err);
      } finally {
        setConsolidatedItemsLoading(false);
      }
    };

    fetchConsolidatedItems();
  }, [isOpen, ticket.isConsolidatedRequest, ticket.consolidatedItemCount, ticket.currentStage, ticket.id]);

  // Determine if ticket was rejected or cancelled (including consolidation-based rejection)
  const currentStageLower = ticket.currentStage.toLowerCase();
  const isRejected =
    ticket.currentStage === 'Rejected' ||
    currentStageLower.includes('rejected');
  const isCancelled =
    ticket.currentStage === 'Cancelled' ||
    currentStageLower.includes('cancelled');

  // Dynamic colors based on approval/rejection/cancellation status
  const statusColor = isRejected ? '#dc2626' : isCancelled ? '#64748b' : '#10b981';
  const statusColorRgba = isRejected ? 'rgba(220, 38, 38' : isCancelled ? 'rgba(100, 116, 139' : 'rgba(16, 185, 129';

  // Generate approval document data from ticket (include fetched consolidated items)
  const approvalDocumentData = useMemo(() => {
    // Determine if this is a consolidated request based on multiple indicators
    const hasConsolidatedItems = consolidatedItems.length > 0 || (ticket.consolidatedItems?.length || 0) > 0;
    const hasConsolidatedCount = ticket.consolidatedItemCount !== undefined && ticket.consolidatedItemCount > 0;
    const stageIndicatesConsolidation = ticket.currentStage.toLowerCase().includes('consolidation');
    const isConsolidated = ticket.isConsolidatedRequest || hasConsolidatedItems || hasConsolidatedCount || stageIndicatesConsolidation;

    // Create a ticket copy with consolidated items and flag included
    const ticketWithItems: DASTicketDetails = {
      ...ticket,
      isConsolidatedRequest: isConsolidated,
      consolidatedItems: consolidatedItems.length > 0 ? consolidatedItems : ticket.consolidatedItems,
      consolidatedItemCount: ticket.consolidatedItemCount || consolidatedItems.length || ticket.consolidatedItems?.length,
    };
    console.log('[CompletedTicketModal] Generating approval document with:', {
      ticketId: ticket.id,
      originalIsConsolidatedRequest: ticket.isConsolidatedRequest,
      computedIsConsolidated: isConsolidated,
      consolidatedItemCount: ticketWithItems.consolidatedItemCount,
      fetchedConsolidatedItemsCount: consolidatedItems.length,
      ticketConsolidatedItemsCount: ticket.consolidatedItems?.length || 0,
      workflowHistoryCount: ticket.workflowHistory?.length || 0,
      approvalHierarchyCount: ticket.approvalHierarchy?.length || 0,
    });
    return transformTicketToApprovalDocument(ticketWithItems);
  }, [ticket, consolidatedItems]);

  if (!isOpen) return null;

  // Tab configuration for completed tickets
  const tabs: TabConfig[] = [
    { id: 'outcome', label: 'Final Outcome', icon: IconSystem.check },
    { id: 'approval-flow', label: 'Approval Flow', icon: IconSystem.users },
    { id: 'history', label: 'History', icon: IconSystem.clock },
    { id: 'attachments', label: 'Attachments', icon: IconSystem.file, badge: ticket.attachments?.length || 0 },
    { id: 'related', label: 'Related', icon: IconSystem.link },
    // Only show Consolidated Items tab for consolidated requests
    ...(ticket.isConsolidatedRequest ? [
      { id: 'consolidated' as TabId, label: 'Consolidated Items', icon: IconSystem.list, badge: ticket.consolidatedItemCount || 0 },
    ] : []),
  ];

  // Handle escape key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  // Render Final Outcome Tab (Main tab for completed tickets)
  const renderFinalOutcomeTab = () => {
    return (
      <div style={{ padding: '1.5rem' }}>
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {/* Final Outcome Section */}
          {ticket.lastActionBy && (
            <div
              style={{
                background: `linear-gradient(135deg, ${statusColorRgba}, 0.1) 0%, ${statusColorRgba}, 0.05) 100%)`,
                borderRadius: '0.75rem',
                padding: '1.5rem',
                border: `2px solid ${statusColor}`,
                boxShadow: `0 4px 6px ${statusColorRgba}, 0.1)`,
              }}
            >
              {/* Header with Final Status Badge */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '1.25rem',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  Final Outcome
                </h3>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1.25rem',
                    background: statusColor,
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: 700,
                    borderRadius: '0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    boxShadow: `0 2px 4px ${statusColorRgba}, 0.3)`,
                  }}
                >
                  {isRejected ? <IconSystem.x size={20} /> : isCancelled ? <IconSystem.xCircle size={20} /> : <IconSystem.check size={20} />}
                  {isRejected ? 'REJECTED' : isCancelled ? 'CANCELLED' : 'APPROVED'}
                </div>
              </div>

              {/* Final Outcome Details Grid */}
              <div
                style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.25rem' }}
              >
                {/* Final Approver */}
                <div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--text-secondary)',
                      marginBottom: '0.5rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}
                  >
                    {isRejected ? 'Rejected By' : isCancelled ? 'Cancelled By' : 'Final Approver'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: statusColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      <IconSystem.user size={20} />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {ticket.lastActionBy}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                        {ticket.workflowHistory && ticket.workflowHistory.length > 0
                          ? ticket.workflowHistory[ticket.workflowHistory.length - 1].actionByRole
                          : 'Final Approver'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Final Action Date */}
                {ticket.lastActionDate && (
                  <div>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--text-secondary)',
                        marginBottom: '0.5rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}
                    >
                      {isRejected ? 'Rejected On' : isCancelled ? 'Cancelled On' : 'Completed On'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <IconSystem.clock size={18} style={{ color: statusColor }} />
                      <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                        {formatDate(ticket.lastActionDate)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Total Ticket Age */}
                {ticket.ticketAge !== undefined && (
                  <div>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--text-secondary)',
                        marginBottom: '0.5rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}
                    >
                      Total Processing Time
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: statusColor }}>
                      {ticket.ticketAge} days
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                      From creation to {isRejected ? 'rejection' : isCancelled ? 'cancellation' : 'completion'}
                    </div>
                  </div>
                )}
              </div>

              {/* Final Comments Section */}
              {ticket.workflowHistory &&
                ticket.workflowHistory.length > 0 &&
                ticket.workflowHistory[ticket.workflowHistory.length - 1].comments && (
                  <div
                    style={{
                      marginTop: '1.25rem',
                      paddingTop: '1.25rem',
                      borderTop: `2px solid ${statusColorRgba}, 0.2)`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--text-secondary)',
                        marginBottom: '0.5rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}
                    >
                      {isRejected ? 'Rejection Reason' : isCancelled ? 'Cancellation Reason' : 'Final Decision Comments'}
                    </div>
                    <div
                      style={{
                        fontSize: '0.9375rem',
                        color: 'var(--text-primary)',
                        lineHeight: 1.6,
                        padding: '0.75rem',
                        background: `${statusColorRgba}, 0.05)`,
                        borderRadius: '0.5rem',
                        borderLeft: `3px solid ${statusColor}`,
                        fontStyle: 'italic',
                      }}
                    >
                      "{ticket.workflowHistory[ticket.workflowHistory.length - 1].comments}"
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* Basic Information */}
          <TicketBasicInfo ticket={ticket} />
        </div>
      </div>
    );
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'outcome':
        return renderFinalOutcomeTab();
      case 'approval-flow':
        return <TicketApprovalFlow approvalHierarchy={ticket.approvalHierarchy || []} ticketStatus={ticket.currentStage} />;
      case 'history':
        return <TicketHistory workflowHistory={ticket.workflowHistory || []} />;
      case 'attachments':
        return <TicketAttachments attachments={ticket.attachments || []} />;
      case 'related':
        return <TicketRelated relatedTickets={ticket.relatedTickets || []} relatedTicketDetails={ticket.relatedTicketDetails} />;
      case 'consolidated':
        return (
          <TicketConsolidatedItems
            consolidatedRequestId={ticket.id.toString()}
            itemCount={ticket.consolidatedItemCount}
            totalValue={ticket.consolidatedTotalValue}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: '1rem',
      }}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '0.5rem',
          width: '100%',
          maxWidth: '1200px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1.5rem',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                {ticket.ticketNo}
              </h2>
              <span
                style={{
                  padding: '0.25rem 0.625rem',
                  background: statusColor,
                  color: 'white',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  borderRadius: '0.25rem',
                  textTransform: 'uppercase',
                }}
              >
                {isRejected ? 'Rejected' : isCancelled ? 'Cancelled' : 'Approved'}
              </span>
            </div>
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              {ticket.subject}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: 'var(--text-secondary)',
            }}
            title="Close"
          >
            <IconSystem.x size={24} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid var(--border-color)',
            padding: '0 1.5rem',
            overflowX: 'auto',
            overflowY: 'hidden',
            flexShrink: 0,
          }}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '1rem 1.5rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: activeTab === tab.id ? 'var(--primary)' : 'var(--text-secondary)',
                  borderBottom: activeTab === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  fontSize: '0.875rem',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                }}
              >
                <Icon size={16} />
                {tab.label}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span
                    style={{
                      background: activeTab === tab.id ? 'var(--primary)' : 'var(--text-muted)',
                      color: 'white',
                      padding: '0.125rem 0.5rem',
                      borderRadius: '1rem',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div style={{ flex: 1, overflow: 'auto' }}>{renderTabContent()}</div>

        {/* Footer */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderTop: '1px solid var(--border-color)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottomLeftRadius: '0.5rem',
            borderBottomRightRadius: '0.5rem',
          }}
        >
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            {isRejected ? 'Rejected' : isCancelled ? 'Cancelled' : 'Completed'}: {formatDate(ticket.lastActionDate)}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {approvalDocumentData && (
              <button
                onClick={() => setShowApprovalDocument(true)}
                disabled={consolidatedItemsLoading}
                style={{
                  padding: '0.5rem 1rem',
                  background: consolidatedItemsLoading ? '#94a3b8' : '#1e40af',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'white',
                  cursor: consolidatedItemsLoading ? 'wait' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  opacity: consolidatedItemsLoading ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!consolidatedItemsLoading) e.currentTarget.style.background = '#1e3a8a';
                }}
                onMouseLeave={(e) => {
                  if (!consolidatedItemsLoading) e.currentTarget.style.background = '#1e40af';
                }}
              >
                <IconSystem.file size={16} />
                {consolidatedItemsLoading ? 'Loading...' : 'Generate Certificate'}
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '0.5rem 1.5rem',
                background: 'transparent',
                border: '1px solid var(--border-color)',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'var(--text-primary)',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--bg-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Approval Document Modal */}
      {approvalDocumentData && (
        <ApprovalDocumentModal
          isOpen={showApprovalDocument}
          onClose={() => setShowApprovalDocument(false)}
          data={approvalDocumentData}
        />
      )}
    </div>
  );
};

export default CompletedTicketModal;
